import { useEffect } from "react";
import { Session, waitForSession } from "@paywithglide/glide-js";
import { CircleCheck, ExternalLink } from "lucide-react";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import PrimaryButton from "../components/PrimaryButton";
import Warning from "../components/Warning";
import { getETAWarning } from "../lib/getETAWarning";
import { logger } from "../lib/logger";

type Props = {
  session: Session;
  onClose: () => void;
  onInsufficientPaymentAmount?: (session: Session) => void;
};

export default function TransactionCheckoutSuccess({
  session: initialSession,
  onClose,
  onInsufficientPaymentAmount,
}: Props) {
  const { glideConfig, mode } = useGlideDepositConfig();

  // Bitcoin lands here on waiting_confirmations, before the deposited amount is
  // final. A converted BTC deposit that comes up short only turns into
  // pending_refund after that, so keep polling until the session settles.
  const isAmountPending =
    mode === "pay" && initialSession.paymentStatus === "waiting_confirmations";

  useEffect(() => {
    if (!isAmountPending || !onInsufficientPaymentAmount) return;

    const abortController = new AbortController();

    waitForSession(glideConfig, {
      sessionId: initialSession.sessionId,
      signal: abortController.signal,
      onUpdate: (updatedSession) => {
        window.parent?.postMessage(
          { type: "sessionUpdate", session: updatedSession },
          "*",
        );

        if (updatedSession.paymentStatus === "paid") {
          abortController.abort();
        }
        if (updatedSession.paymentStatus === "pending_refund") {
          abortController.abort();
          onInsufficientPaymentAmount(updatedSession);
        }
      },
    }).catch((error) => {
      if (error?.name === "AbortError") return;
      logger.error(`[TransactionCheckoutSuccess] Polling error: ${error}`);
    });

    return () => {
      abortController.abort();
    };
  }, [
    isAmountPending,
    glideConfig,
    initialSession.sessionId,
    onInsufficientPaymentAmount,
  ]);

  return (
    <div className="flex flex-col items-center justify-center pt-4">
      <CircleCheck className="h-12 w-12 text-green-600" />
      <div className="mt-2 font-medium text-lg text-center text-[--color-text-primary]">
        Success
      </div>
      <div className="mt-1 text-sm text-[--color-text-secondary] text-center">
        Your{" "}
        {mode === "deposit"
          ? "deposit"
          : mode === "withdraw"
            ? "withdrawal"
            : mode === "pay" || mode === "buy"
              ? "payment"
              : mode === "call"
                ? "transaction"
                : null}{" "}
        was completed successfully.
      </div>
      <a
        href={`https://explorer.paywithglide.xyz/?session_id=${initialSession.sessionId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 text-sm text-blue-500 hover:underline flex items-center gap-1"
      >
        View transaction details
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
      {getETAWarning(mode, initialSession.etaInSeconds) ? (
        <Warning className="mt-4">
          {getETAWarning(mode, initialSession.etaInSeconds)}
        </Warning>
      ) : null}
      <div className="mt-4 w-full">
        <PrimaryButton onClick={onClose}>Done</PrimaryButton>
      </div>
    </div>
  );
}
